import type { AgentVisualizationData } from "./AgentVisualization";

export type ChartType = "bar" | "line" | "pie" | "bubble";
export type Aggregation = "sum" | "average" | "count" | "min" | "max";
export type SortDirection = "asc" | "desc";

export type ChartColumn = AgentVisualizationData["columns"][number];

export type ChartFilter =
  | { kind: "category"; field: string; value: unknown }
  | { kind: "number"; field: string; min?: number; max?: number };

export type ChartSort = {
  by: "category" | "value";
  direction: SortDirection;
  /** Limits value sorting to one measure. All value fields are combined when omitted. */
  valueField?: string;
  /** Limits value sorting to one series. All series are combined when omitted. */
  seriesValue?: unknown;
};

export type ChartBuilderConfig = {
  chartType: ChartType;
  categoryField?: string;
  valueFields: string[];
  seriesField?: string;
  xField?: string;
  yField?: string;
  sizeField?: string;
  labelField?: string;
  colorField?: string;
  aggregation: Aggregation;
  filters: ChartFilter[];
  sort: ChartSort;
  topN?: number;
};

export type PivotRow = {
  /** Display-ready category label. */
  category: string;
  /** Original value, retained for editors and drill-down behavior. */
  categoryValue: unknown;
  /** Keys are produced by pivotSeriesKey. Missing combinations are represented by null. */
  values: Record<string, number | null>;
};

export type PivotSeries = {
  key: string;
  label: string;
  valueField: string;
  seriesValue?: unknown;
};

export type BubblePoint = {
  x: number;
  y: number;
  size: number;
  label: string;
  color?: string;
  rowIndex: number;
  sourceRow: Record<string, unknown>;
};

const NUMERIC_COLUMN_TYPES = new Set([
  "number",
  "numeric",
  "integer",
  "int",
  "float",
  "double",
  "decimal",
  "currency",
  "percentage",
  "percent",
]);

export const isNumericColumn = (column: ChartColumn): boolean =>
  NUMERIC_COLUMN_TYPES.has(column.type.trim().toLowerCase());

export const isDimensionColumn = (column: ChartColumn): boolean => !isNumericColumn(column);

/**
 * Converts common API and CSV numeric values without turning blanks or booleans into zero.
 * Currency symbols, thousands separators, percentages, and accounting negatives are accepted.
 */
export const coerceFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  let text = value.trim();
  if (!text) return null;

  const accountingNegative = /^\(.*\)$/.test(text);
  if (accountingNegative) text = text.slice(1, -1).trim();
  text = text
    .replace(/,/g, "")
    .replace(/[$£€¥]/g, "")
    .replace(/%$/, "")
    .trim();

  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i.test(text)) return null;
  const number = Number(text);
  if (!Number.isFinite(number)) return null;
  return accountingNegative ? -Math.abs(number) : number;
};

const columnByKey = (data: AgentVisualizationData, key?: string) =>
  key ? data.columns.find((column) => column.key === key) : undefined;

const unique = <T,>(items: T[]): T[] => Array.from(new Set(items));

export const createDefaultChartConfig = (data: AgentVisualizationData): ChartBuilderConfig => {
  const numericFields = unique([
    ...data.measures,
    ...data.columns.filter(isNumericColumn).map((column) => column.key),
  ]).filter((key) => Boolean(columnByKey(data, key)));
  const dimensionFields = unique([
    ...data.dimensions,
    ...data.columns.filter(isDimensionColumn).map((column) => column.key),
  ]).filter((key) => Boolean(columnByKey(data, key)));
  const chartType: ChartType = data.recommended_chart_type;
  const isPie = chartType === "pie";
  const primaryUnit = numericFields[0]
    ? (data.units?.[numericFields[0]] ?? columnByKey(data, numericFields[0])?.unit ?? "")
    : "";
  const scaleCompatibleFields = numericFields.filter((key) =>
    (data.units?.[key] ?? columnByKey(data, key)?.unit ?? "") === primaryUnit
  );

  return {
    chartType,
    categoryField: dimensionFields[0],
    // Start with measures that can share one honest axis. Users can still add
    // differently formatted measures deliberately from the field list.
    valueFields: isPie ? numericFields.slice(0, 1) : scaleCompatibleFields,
    seriesField: !isPie ? dimensionFields[1] : undefined,
    xField: numericFields[0],
    yField: numericFields[1] ?? numericFields[0],
    sizeField: numericFields[2] ?? numericFields[0],
    labelField: dimensionFields[0],
    colorField: dimensionFields[1],
    aggregation: "sum",
    filters: [],
    sort: { by: "category", direction: "asc" },
  };
};

const fieldMessage = (label: string, key: string | undefined, data: AgentVisualizationData, numeric = false) => {
  if (!key) return `${label} is required.`;
  const column = columnByKey(data, key);
  if (!column) return `${label} “${key}” is not available in this result.`;
  if (numeric && !isNumericColumn(column)) return `${label} must be a numeric field.`;
  return undefined;
};

export const validateChartConfig = (
  data: AgentVisualizationData,
  config: ChartBuilderConfig,
): string[] => {
  const messages: string[] = [];

  if (config.chartType === "bubble") {
    [
      fieldMessage("X axis", config.xField, data, true),
      fieldMessage("Y axis", config.yField, data, true),
      fieldMessage("Bubble size", config.sizeField, data, true),
    ].forEach((message) => message && messages.push(message));
    [["Bubble label", config.labelField], ["Bubble color", config.colorField]].forEach(([label, key]) => {
      if (key && !columnByKey(data, key)) messages.push(`${label} “${key}” is not available in this result.`);
    });
  } else {
    const categoryMessage = fieldMessage("Category", config.categoryField, data);
    if (categoryMessage) messages.push(categoryMessage);
    if (!config.valueFields.length) {
      messages.push("At least one value field is required.");
    } else {
      config.valueFields.forEach((key) => {
        const message = fieldMessage("Value field", key, data, true);
        if (message) messages.push(message);
      });
    }
    if (config.chartType === "pie" && config.valueFields.length > 1) {
      messages.push("Pie charts support one value field.");
    }
    if (config.seriesField && !columnByKey(data, config.seriesField)) {
      messages.push(`Series field “${config.seriesField}” is not available in this result.`);
    }
  }

  config.filters.forEach((filter) => {
    const column = columnByKey(data, filter.field);
    if (!column) {
      messages.push(`Filter field “${filter.field}” is not available in this result.`);
    } else if (filter.kind === "number" && !isNumericColumn(column)) {
      messages.push(`Numeric filter “${filter.field}” requires a numeric field.`);
    }
    if (filter.kind === "number" && filter.min != null && filter.max != null && filter.min > filter.max) {
      messages.push(`Numeric filter “${filter.field}” has a minimum greater than its maximum.`);
    }
  });

  if (config.topN != null && (!Number.isInteger(config.topN) || config.topN <= 0)) {
    messages.push("Top N must be a positive whole number.");
  }
  return unique(messages);
};

const categoryEquals = (left: unknown, right: unknown): boolean => {
  if (left == null || right == null) return left == null && right == null;
  if (Object.is(left, right)) return true;
  // Select controls commonly return strings even when a category originated as a number.
  return String(left) === String(right);
};

export const applyChartFilters = (
  data: AgentVisualizationData,
  config: ChartBuilderConfig,
): Record<string, unknown>[] => data.rows.filter((row) => config.filters.every((filter) => {
  if (filter.kind === "category") return categoryEquals(row[filter.field], filter.value);
  if (filter.min == null && filter.max == null) return true;
  const value = coerceFiniteNumber(row[filter.field]);
  if (value == null) return false;
  if (filter.min != null && value < filter.min) return false;
  if (filter.max != null && value > filter.max) return false;
  return true;
}));

const stableValueKey = (value: unknown): string => {
  if (value == null) return "null:";
  if (typeof value === "string") return `string:${value}`;
  if (typeof value === "number") return `number:${String(value)}`;
  if (typeof value === "boolean") return `boolean:${String(value)}`;
  try {
    return `json:${JSON.stringify(value)}`;
  } catch {
    return `value:${String(value)}`;
  }
};

const displayValue = (value: unknown): string => {
  if (value == null || value === "") return "Unspecified";
  return String(value);
};

/** Produces a collision-safe object key for a value field and optional series value. */
export const pivotSeriesKey = (valueField: string, seriesValue?: unknown): string =>
  seriesValue === undefined
    ? valueField
    : JSON.stringify([stableValueKey(seriesValue), valueField]);

type AggregateState = {
  sum: number;
  numericCount: number;
  presentCount: number;
  min: number;
  max: number;
};

const newAggregateState = (): AggregateState => ({
  sum: 0,
  numericCount: 0,
  presentCount: 0,
  min: Number.POSITIVE_INFINITY,
  max: Number.NEGATIVE_INFINITY,
});

const addToAggregate = (state: AggregateState, value: unknown) => {
  if (value != null && !(typeof value === "string" && value.trim() === "")) state.presentCount += 1;
  const number = coerceFiniteNumber(value);
  if (number == null) return;
  state.sum += number;
  state.numericCount += 1;
  state.min = Math.min(state.min, number);
  state.max = Math.max(state.max, number);
};

const finishAggregate = (state: AggregateState, aggregation: Aggregation): number | null => {
  if (aggregation === "count") return state.presentCount;
  if (!state.numericCount) return null;
  if (aggregation === "average") return state.sum / state.numericCount;
  if (aggregation === "min") return state.min;
  if (aggregation === "max") return state.max;
  return state.sum;
};

export const getPivotSeries = (
  data: AgentVisualizationData,
  config: ChartBuilderConfig,
): PivotSeries[] => {
  const fields = unique(config.valueFields).filter((field) => Boolean(columnByKey(data, field)));
  if (!config.seriesField) {
    return fields.map((field) => ({
      key: pivotSeriesKey(field),
      label: columnByKey(data, field)?.label ?? field,
      valueField: field,
    }));
  }
  const seriesValues: unknown[] = [];
  const seen = new Set<string>();
  applyChartFilters(data, config).forEach((row) => {
    const value = row[config.seriesField as string];
    const key = stableValueKey(value);
    if (!seen.has(key)) {
      seen.add(key);
      seriesValues.push(value);
    }
  });
  return seriesValues.flatMap((seriesValue) => fields.map((field) => ({
    key: pivotSeriesKey(field, seriesValue),
    label: fields.length === 1
      ? displayValue(seriesValue)
      : `${displayValue(seriesValue)} · ${columnByKey(data, field)?.label ?? field}`,
    valueField: field,
    seriesValue,
  })));
};

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const sortNumber = (values: Array<number | null | undefined>): number | null => {
  const present = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  return present.length ? present.reduce((total, value) => total + value, 0) : null;
};

const sortedAndLimited = <T,>(
  items: T[],
  config: ChartBuilderConfig,
  category: (item: T) => string,
  value: (item: T) => number | null,
): T[] => {
  const direction = config.sort.direction === "desc" ? -1 : 1;
  const sorted = [...items].sort((left, right) => {
    if (config.sort.by === "category") return collator.compare(category(left), category(right)) * direction;
    const leftValue = value(left);
    const rightValue = value(right);
    if (leftValue == null && rightValue == null) return collator.compare(category(left), category(right));
    if (leftValue == null) return 1;
    if (rightValue == null) return -1;
    const difference = (leftValue - rightValue) * direction;
    return difference || collator.compare(category(left), category(right));
  });
  return config.topN == null ? sorted : sorted.slice(0, Math.max(0, Math.floor(config.topN)));
};

export const buildPivotRows = (
  data: AgentVisualizationData,
  config: ChartBuilderConfig,
): PivotRow[] => {
  if (!config.categoryField || !columnByKey(data, config.categoryField) || !config.valueFields.length) return [];

  type CategoryBucket = {
    category: string;
    categoryValue: unknown;
    aggregates: Map<string, AggregateState>;
  };
  const buckets = new Map<string, CategoryBucket>();
  const fields = unique(config.valueFields).filter((field) => Boolean(columnByKey(data, field)));
  const series = getPivotSeries(data, config);

  applyChartFilters(data, config).forEach((row) => {
    const categoryValue = row[config.categoryField as string];
    const categoryKey = stableValueKey(categoryValue);
    let bucket = buckets.get(categoryKey);
    if (!bucket) {
      bucket = { category: displayValue(categoryValue), categoryValue, aggregates: new Map() };
      buckets.set(categoryKey, bucket);
    }
    const seriesValue = config.seriesField ? row[config.seriesField] : undefined;
    fields.forEach((field) => {
      const key = pivotSeriesKey(field, seriesValue);
      let state = bucket?.aggregates.get(key);
      if (!state) {
        state = newAggregateState();
        bucket?.aggregates.set(key, state);
      }
      addToAggregate(state, row[field]);
    });
  });

  const rows = Array.from(buckets.values()).map<PivotRow>((bucket) => ({
    category: bucket.category,
    categoryValue: bucket.categoryValue,
    values: Object.fromEntries(series.map((item) => [
      item.key,
      bucket.aggregates.has(item.key)
        ? finishAggregate(bucket.aggregates.get(item.key) as AggregateState, config.aggregation)
        : null,
    ])),
  }));

  const relevantSeries = series.filter((item) => {
    if (config.sort.valueField && item.valueField !== config.sort.valueField) return false;
    if (config.sort.seriesValue !== undefined && !categoryEquals(item.seriesValue, config.sort.seriesValue)) return false;
    return true;
  });
  return sortedAndLimited(
    rows,
    config,
    (row) => row.category,
    (row) => sortNumber(relevantSeries.map((item) => row.values[item.key])),
  );
};

export const buildBubblePoints = (
  data: AgentVisualizationData,
  config: ChartBuilderConfig,
): BubblePoint[] => {
  if (!config.xField || !config.yField || !config.sizeField) return [];
  if (![config.xField, config.yField, config.sizeField].every((field) => Boolean(columnByKey(data, field)))) return [];

  const points = applyChartFilters(data, config).flatMap<BubblePoint>((row) => {
    const x = coerceFiniteNumber(row[config.xField as string]);
    const y = coerceFiniteNumber(row[config.yField as string]);
    const size = coerceFiniteNumber(row[config.sizeField as string]);
    if (x == null || y == null || size == null) return [];
    const sourceRowIndex = data.rows.indexOf(row);
    const labelValue = config.labelField ? row[config.labelField] : undefined;
    const colorValue = config.colorField ? row[config.colorField] : undefined;
    return [{
      x,
      y,
      size: Math.max(0, size),
      label: labelValue == null || labelValue === "" ? `Point ${sourceRowIndex + 1}` : String(labelValue),
      color: colorValue == null || colorValue === "" ? undefined : String(colorValue),
      rowIndex: sourceRowIndex,
      sourceRow: row,
    }];
  });

  return sortedAndLimited(
    points,
    config,
    (point) => point.label,
    (point) => point.size,
  );
};
